import {
  Component,
  ElementRef,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
  viewChild
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { formatFileSize } from '@awo-care/utils';

@Component({
  selector: 'lib-file-drop-zone',
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule],
  template: `
    <input
      #fileInput
      type="file"
      [accept]="accept()"
      (change)="onFileSelected($event)"
      style="display: none;"
    />

    @if (!selectedFile()) {
      <div
        class="drop-zone"
        [class.drag-over]="isDragOver()"
        (click)="triggerFileInput()"
        (keyup.enter)="triggerFileInput()"
        (keyup.space)="triggerFileInput()"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        tabindex="0"
        role="button"
        [attr.aria-label]="dropZoneLabel()"
      >
        <mat-icon>cloud_upload</mat-icon>
        <h3>{{ dropZoneTitle() }}</h3>
        <p>{{ dropZoneHint() }}</p>
      </div>
    }

    @if (selectedFile()) {
      <div class="selected-file">
        <div class="file-info">
          <mat-icon>picture_as_pdf</mat-icon>
          <span>{{ selectedFile()?.name }}</span>
          <span class="file-size">
            ({{ formatFileSize(selectedFile()?.size || 0) }})
          </span>
        </div>
        <div class="file-actions">
          <button mat-button (click)="clear()" [disabled]="uploading()">
            <mat-icon>close</mat-icon>
            Abbrechen
          </button>
          <button
            mat-raised-button
            (click)="onUploadClick()"
            [disabled]="uploading() || uploadDisabled()"
          >
            <mat-icon>cloud_upload</mat-icon>
            @if (uploading()) {
              <ng-container>Hochladen...</ng-container>
            } @else {
              <ng-container>Hochladen</ng-container>
            }
          </button>
        </div>
      </div>
      @if (uploading()) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .drop-zone {
        border: 2px dashed var(--theme-border);
        border-radius: var(--border-radius);
        padding: 3rem 2rem;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        background-color: #fafafa;

        &:hover {
          border-color: var(--theme-primary);
          background-color: #f0f7ff;
        }

        &.drag-over {
          border-color: var(--theme-primary);
          background-color: var(--theme-info-surface);
          transform: scale(1.02);
        }

        mat-icon {
          font-size: 48px;
          width: 48px;
          height: 48px;
          color: var(--dashboard-stats-text-secondary);
          margin-bottom: 1rem;
        }

        h3 {
          margin: 0 0 0.5rem 0;
          font-weight: 500;
        }

        p {
          margin: 0;
          color: var(--dashboard-stats-text-secondary);
          font-size: 0.875rem;
        }

        &:hover mat-icon,
        &.drag-over mat-icon {
          color: var(--theme-primary);
        }
      }

      .selected-file {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: var(--border-radius);
        border: 1px solid var(--theme-border);

        .file-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          mat-icon {
            color: var(--theme-primary);
          }

          .file-size {
            color: var(--dashboard-stats-text-secondary);
            font-size: 0.875rem;
          }
        }

        .file-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: center;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        }
      }
    `,
  ],
})
export class FileDropZoneComponent {
  readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  accept = input('.pdf');
  maxSizeMb = input(10);
  dropZoneTitle = input('PDF-Datei hier ablegen');
  dropZoneHint = input('oder klicken zum Auswählen');
  dropZoneLabel = input('Datei auswählen oder hier ablegen');
  uploading = input(false);
  uploadDisabled = input(false);

  fileSelected = output<File>();
  fileCleared = output<void>();
  uploadClicked = output<File>();

  selectedFile = signal<File | null>(null);
  isDragOver = signal(false);

  triggerFileInput() {
    this.fileInput().nativeElement.click();
  }

  clear() {
    this.selectedFile.set(null);
    const fileInput = this.fileInput();
    if (fileInput) {
      fileInput.nativeElement.value = '';
    }
    this.fileCleared.emit();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      this.processFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  onUploadClick() {
    const file = this.selectedFile();
    if (file) {
      this.uploadClicked.emit(file);
    }
  }

  formatFileSize(bytes: number): string {
    return formatFileSize(bytes);
  }

  private processFile(file: File) {
    const acceptedTypes = this.accept()
      .split(',')
      .map((t) => t.trim());
    const isValidType = acceptedTypes.some(
      (type) =>
        file.name.toLowerCase().endsWith(type) ||
        file.type === this.mimeFromExtension(type)
    );

    if (!isValidType) {
      return;
    }

    if (file.size > this.maxSizeMb() * 1024 * 1024) {
      return;
    }

    this.selectedFile.set(file);
    this.fileSelected.emit(file);
  }

  private mimeFromExtension(ext: string): string {
    const map: Record<string, string> = { '.pdf': 'application/pdf' };
    return map[ext] || '';
  }
}
